import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Megaphone, Loader2, Trash2, Info, AlertTriangle, Eye, Clock } from 'lucide-react';
import { toast } from 'sonner';

const DURATIONS = [
  { value: '24', label: '24 hours' },
  { value: '72', label: '3 days' },
  { value: '168', label: '1 week' },
  { value: 'custom', label: 'Custom date/time' },
];

const STATUS_STYLE = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  expired: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

export const BannerBroadcastDialog = ({ open, onOpenChange, api, selectedUserIds, formatDT }) => {
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [mode, setMode] = useState('once');
  const [duration, setDuration] = useState('24');
  const [customExpiry, setCustomExpiry] = useState('');
  const [targetAll, setTargetAll] = useState(false);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState([]);
  const [loadingSent, setLoadingSent] = useState(false);

  const loadSent = useCallback(async () => {
    setLoadingSent(true);
    try {
      const r = await api.get('/admin/banners');
      setSent(r.data || []);
    } catch { /* non-blocking */ }
    setLoadingSent(false);
  }, [api]);

  useEffect(() => {
    if (open) {
      setMessage(''); setSeverity('info'); setMode('once'); setDuration('24'); setCustomExpiry('');
      setLinkLabel(''); setLinkUrl('');
      setTargetAll(selectedUserIds.length === 0);
      loadSent();
    }
  }, [open, selectedUserIds.length, loadSent]);

  const handleSend = async () => {
    if (!message.trim()) { toast.error('Enter a banner message'); return; }
    if (linkUrl.trim() && !/^https?:\/\//i.test(linkUrl.trim())) {
      toast.error('Button link must start with http:// or https://');
      return;
    }
    let expiresAt = null;
    if (mode === 'timed') {
      if (duration === 'custom') {
        if (!customExpiry) { toast.error('Pick an expiry date/time'); return; }
        expiresAt = new Date(customExpiry).toISOString();
      } else {
        expiresAt = new Date(Date.now() + Number(duration) * 3600 * 1000).toISOString();
      }
    }
    setSending(true);
    try {
      const r = await api.post('/admin/banners', {
        message: message.trim(),
        severity,
        mode,
        expires_at: expiresAt,
        target_all: targetAll,
        user_ids: targetAll ? [] : selectedUserIds,
        link_label: linkLabel.trim() || null,
        link_url: linkUrl.trim() || null,
      });
      toast.success(`Banner sent to ${r.data?.target_count ?? 0} user(s)`);
      setMessage(''); setLinkLabel(''); setLinkUrl('');
      loadSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send banner');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.delete(`/admin/banners/${id}`);
      toast.success('Banner cancelled');
      loadSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel banner');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="banner-broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone className="w-4 h-4" /> Send Banner Message</DialogTitle>
          <DialogDescription>Show a message strip at the top of the app for the chosen users.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="compose">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="compose" data-testid="banner-tab-compose">Compose</TabsTrigger>
            <TabsTrigger value="sent" data-testid="banner-tab-sent">Sent Banners</TabsTrigger>
          </TabsList>

          <TabsContent value="compose" className="space-y-4 pt-2">
            <Textarea
              data-testid="banner-message-input"
              placeholder="Type the banner message (max 500 characters)..."
              value={message}
              maxLength={500}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground w-16">Style</span>
              <Button size="sm" variant={severity === 'info' ? 'default' : 'outline'}
                className={severity === 'info' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                onClick={() => setSeverity('info')} data-testid="banner-severity-info">
                <Info className="w-3.5 h-3.5 mr-1" /> Info (blue)
              </Button>
              <Button size="sm" variant={severity === 'warning' ? 'destructive' : 'outline'}
                onClick={() => setSeverity('warning')} data-testid="banner-severity-warning">
                <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Warning (red)
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground w-16">Display</span>
              <Button size="sm" variant={mode === 'once' ? 'default' : 'outline'} onClick={() => setMode('once')} data-testid="banner-mode-once">
                Show once (until dismissed)
              </Button>
              <Button size="sm" variant={mode === 'timed' ? 'default' : 'outline'} onClick={() => setMode('timed')} data-testid="banner-mode-timed">
                Time-based (sticks until expiry)
              </Button>
            </div>
            {mode === 'timed' && (
              <div className="flex flex-wrap items-center gap-2 pl-[72px]">
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="w-44 h-8" data-testid="banner-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map(d => <SelectItem key={d.value} value={d.value} data-testid={`banner-duration-${d.value}`}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {duration === 'custom' && (
                  <Input type="datetime-local" className="w-56 h-8" value={customExpiry}
                    min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                    onChange={(e) => setCustomExpiry(e.target.value)} data-testid="banner-custom-expiry" />
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground w-16">Button</span>
              <Input className="w-36 h-8" placeholder="Label (Read more)" maxLength={40}
                value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} data-testid="banner-link-label" />
              <Input className="flex-1 min-w-[180px] h-8" placeholder="https://... (optional — adds a button to the banner)"
                value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} data-testid="banner-link-url" />
            </div>
            <div className="flex items-center gap-3 border-t pt-3">
              <span className="text-xs text-muted-foreground w-16">Audience</span>
              <label htmlFor="banner-target-all-cb" className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox id="banner-target-all-cb" checked={targetAll} onCheckedChange={(v) => setTargetAll(!!v)} data-testid="banner-target-all" />
                Send to all users
              </label>
              {!targetAll && (
                <Badge variant="secondary" data-testid="banner-selected-count">{selectedUserIds.length} selected user(s)</Badge>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
              <Button size="sm" onClick={handleSend}
                disabled={sending || !message.trim() || (!targetAll && selectedUserIds.length === 0)}
                className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="banner-send-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-4 h-4 mr-2" />}
                Send Banner
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="sent" className="pt-2">
            <ScrollArea className="h-[320px] pr-2">
              {loadingSent ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : sent.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No banners sent yet.</p>
              ) : (
                <div className="space-y-2">
                  {sent.map(b => (
                    <div key={b.id} className="border rounded-lg p-3 space-y-1.5" data-testid={`sent-banner-${b.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium break-words min-w-0">{b.message}</p>
                        {b.status === 'active' && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 shrink-0"
                            onClick={() => handleCancel(b.id)} data-testid={`cancel-banner-${b.id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Badge className={`text-[10px] ${STATUS_STYLE[b.status] || ''}`} variant="secondary">{b.status}</Badge>
                        <Badge variant="outline" className={`text-[10px] ${b.severity === 'warning' ? 'text-red-600 border-red-300' : 'text-blue-600 border-blue-300'}`}>
                          {b.severity === 'warning' ? 'warning' : 'info'}
                        </Badge>
                        <span>{b.mode === 'timed' ? `until ${formatDT ? formatDT(b.expires_at) : b.expires_at}` : 'show once'}</span>
                        {b.link_url && <span className="truncate max-w-[160px]" title={b.link_url}>🔗 {b.link_label || 'Read more'} · clicks {b.clicked_count ?? 0}</span>}
                        <span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3" /> {b.seen_count}/{b.target_count}</span>
                        {b.mode === 'once' && <span>· dismissed {b.dismissed_count}</span>}
                        <span className="inline-flex items-center gap-0.5"><Clock className="w-3 h-3" /> {formatDT ? formatDT(b.created_at) : b.created_at}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
